// views/device/DeviceFormView.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createDeviceApi,
  updateDeviceApi,
  getDeviceDetailApi,
  uploadDevicePhotoApi,
  checkDeviceIdApi,
} from "@/lib/api";
import { getImageUrl } from "@/lib/api/device";
import { useLoading } from "@/store/loadingStore";
import { useModal } from "@/store/modalStore";
import MapComponent from "@/components/MapComponent"; // 지도 컴포넌트
import { regions, findChildRegions, getRegionByCode, findNearestRegion } from "@/constants/regions";
import "./DeviceForm.css";

interface FormData {
  // 기기정보
  deviceId: string;
  brand: string;
  measurementMethod: string;
  measurementItems: string;
  sensingMethod: string;
  purchaser: string;
  warrantyPeriod: string;
  purchaseLocation: string;
  vendor: string;
  sellerName: string;
  sellerContact: string;
  sellerEmail: string;
  otherInfo: string;
  locationNote: string;

  // 설치정보
  regionProvince: string;
  regionCity: string;
  regionDistrict: string;
  locationDetail: string;
  latitude: string;
  longitude: string;
  deviceName: string;
  deviceModel: string;
  installationDate: string;
  installer: string;
  installerContact: string;
  inspectionNotes: string;
}

interface PhotoFile {
  file: File | null;
  preview: string;
  id?: number; // 수정 모드에서 기존 사진 ID
}

const DeviceFormView = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // URL 파라미터에서 ID 가져오기
  const isEditMode = !!id; // 수정 모드 여부

  const { startLoading, stopLoading } = useLoading();
  const modal = useModal();

  // 폼 데이터
  const [formData, setFormData] = useState<FormData>({
    deviceId: "",
    brand: "",
    measurementMethod: "전기화학식",
    measurementItems: "H2S, NH3, VOCs, TEMP, HUMIDITY",
    sensingMethod: "감체 흡입식",
    purchaser: "",
    warrantyPeriod: "구입일로부터 2년",
    purchaseLocation: "",
    vendor: "",
    sellerName: "",
    sellerContact: "",
    sellerEmail: "",
    otherInfo: "",
    locationNote: "",
    regionProvince: "전라남도",
    regionCity: "나주시",
    regionDistrict: "",
    locationDetail: "",
    latitude: "",
    longitude: "",
    deviceName: "",
    deviceModel: "",
    installationDate: "",
    installer: "",
    installerContact: "",
    inspectionNotes: "",
  });

  // 사진 파일
  const [photos, setPhotos] = useState<PhotoFile[]>([
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
  ]);

  // 지역 목록
  const [cities, setCities] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);

  // 에러 상태
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 아이디 중복 체크 상태
  const [isDeviceIdChecked, setIsDeviceIdChecked] = useState(false);
  const [isDeviceIdAvailable, setIsDeviceIdAvailable] = useState(false);

  // 나주시 지역 데이터만 필터링
  const najuRegions = regions.filter((region) => region.fullName.includes("나주시"));

  // 나주시 읍면동 목록 (코드가 4617로 시작하고 000으로 끝나는 지역들)
  const najuDistricts = najuRegions
    .filter(
      (region) =>
        region.code.startsWith("4617") &&
        region.code.endsWith("000") &&
        region.code !== "4617000000" // 나주시 자체는 제외
    )
    .sort((a, b) => {
      // 면 -> 읍 -> 동 순서로 정렬
      const order = ["면", "읍", "동"];
      const aType = order.findIndex((type) => a.name.endsWith(type));
      const bType = order.findIndex((type) => b.name.endsWith(type));

      if (aType !== bType) return aType - bType;
      return a.name.localeCompare(b.name);
    });

  // 시/도는 고정 (전라남도)
  const province = "전라남도";

  // 시/군/구는 고정 (나주시)
  const city = "나주시";

  // 선택된 읍면동의 지역 코드 상태 추가
  const [selectedDistrictCode, setSelectedDistrictCode] = useState<string>("");

  // 수정 모드일 때 데이터 로드
  useEffect(() => {
    if (isEditMode && id) {
      loadDeviceData(parseInt(id));
    }
  }, [isEditMode, id]);

  // 선택된 읍면동에 따른 하위 리 목록 로드
  useEffect(() => {
    if (formData.regionDistrict) {
      // 선택된 읍면동의 코드 찾기
      const selectedDistrict = najuDistricts.find((d) => d.name === formData.regionDistrict);
      if (selectedDistrict) {
        setSelectedDistrictCode(selectedDistrict.code);
        // 해당 읍면동의 하위 리 목록 찾기
        const childRegions = findChildRegions(selectedDistrict.code);
        setDistricts(childRegions);
      }
    } else {
      setSelectedDistrictCode("");
      setDistricts([]);
    }
  }, [formData.regionDistrict]);

  // 기기 데이터 로드 (수정 모드)
  const loadDeviceData = async (deviceId: number) => {
    try {
      startLoading("기기 정보를 불러오는 중...");
      const response = await getDeviceDetailApi(deviceId);

      // 폼 데이터 설정
      setFormData({
        deviceId: response.deviceId,
        brand: response.brand || "",
        measurementMethod: response.measurementMethod || "전기화학식",
        measurementItems: response.measurementItems || "",
        sensingMethod: response.sensingMethod || "감체 흡입식",
        purchaser: response.purchaser || "",
        warrantyPeriod: response.warrantyPeriod || "구입일로부터 2년",
        purchaseLocation: response.purchaseLocation || "",
        vendor: response.vendor || "",
        sellerName: response.sellerName || "",
        sellerContact: response.sellerContact || "",
        sellerEmail: response.sellerEmail || "",
        otherInfo: response.otherInfo || "",
        locationNote: response.locationNote || "",
        regionProvince: response.installation?.regionProvince || "전라남도",
        regionCity: response.installation?.regionCity || "나주시",
        regionDistrict: response.installation?.regionDistrict || "",
        locationDetail: response.installation?.locationDetail || "",
        latitude: response.installation?.latitude?.toString() || "",
        longitude: response.installation?.longitude?.toString() || "",
        deviceName: response.installation?.deviceName || "",
        deviceModel: response.installation?.deviceModel || "",
        installationDate: response.installation?.installationDate || "",
        installer: response.installation?.installer || "",
        installerContact: response.installation?.installerContact || "",
        inspectionNotes: response.installation?.inspectionNotes || "",
      });

      // 사진 설정
      if (response.photos && response.photos.length > 0) {
        const newPhotos = [...photos];
        response.photos.forEach((photo: any, index: number) => {
          if (index < 3) {
            // 디버깅을 위한 상세 로그
            console.log(`Photo ${index} 처리:`, {
              원본: photo,
              filePath: photo.filePath,
              fileUrl: photo.fileUrl,
              변환된URL: getImageUrl(photo.filePath),
            });

            newPhotos[index] = {
              file: null,
              preview: getImageUrl(photo.filePath || photo.fileUrl),
              id: photo.id,
            };
          }
        });
        setPhotos(newPhotos);
      }

      // 수정 모드에서는 아이디 중복 체크 불필요
      setIsDeviceIdChecked(true);
      setIsDeviceIdAvailable(true);
    } catch (error) {
      modal.alert("오류", "기기 정보를 불러오는데 실패했습니다.");
      navigate("/device");
    } finally {
      stopLoading();
    }
  };

  // DeviceFormView.tsx에 디버깅 코드 추가

  // najuDistricts 데이터 확인 (컴포넌트 상단에 추가)
  useEffect(() => {
    console.log("najuDistricts 데이터:", najuDistricts);
    if (najuDistricts.length > 0) {
      console.log("첫 번째 district 예시:", najuDistricts[0]);
      console.log("속성 확인:", {
        hasLatitude: "latitude" in najuDistricts[0],
        hasLongitude: "longitude" in najuDistricts[0],
        hasLat: "lat" in najuDistricts[0],
        hasLng: "lng" in najuDistricts[0],
        keys: Object.keys(najuDistricts[0]),
      });
    }
  }, [najuDistricts]);

  // handleChange 함수 수정 - 속성명 확인
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // 읍면동 선택 시 해당 지역의 중심 좌표로 설정
    if (name === "regionDistrict" && value) {
      const selectedDistrict = najuDistricts.find((d) => d.name === value);
      console.log("선택된 district 전체 데이터:", selectedDistrict);

      if (selectedDistrict) {
        // 다양한 속성명 시도
        const lat = selectedDistrict.lat;
        const lng = selectedDistrict.lng;

        console.log("추출된 좌표:", { lat, lng });

        if (lat && lng) {
          setFormData((prev) => ({
            ...prev,
            [name]: value,
            latitude: String(lat),
            longitude: String(lng),
          }));

          // mapCenter 강제 업데이트
          setTimeout(() => {
            setMapCenter({
              lat: Number(lat),
              lng: Number(lng),
            });
          }, 100);
        } else {
          console.error("좌표를 찾을 수 없습니다:", selectedDistrict);
          setFormData((prev) => ({
            ...prev,
            [name]: value,
          }));
        }
      }
    }
    // 리(里) 선택 시 해당 지역의 좌표로 설정
    else if (name === "locationDetail" && value) {
      const selectedRi = districts.find((d) => d.name === value);
      console.log("선택된 리 전체 데이터:", selectedRi);

      if (selectedRi) {
        // 다양한 속성명 시도
        let lat = selectedRi.latitude || selectedRi.lat || selectedRi.y;
        let lng = selectedRi.longitude || selectedRi.lng || selectedRi.lon || selectedRi.x;

        console.log("추출된 좌표:", { lat, lng });

        if (lat && lng) {
          setFormData((prev) => ({
            ...prev,
            [name]: value,
            latitude: String(lat),
            longitude: String(lng),
          }));

          // mapCenter 강제 업데이트
          setTimeout(() => {
            setMapCenter({
              lat: Number(lat),
              lng: Number(lng),
            });
          }, 100);
        } else {
          console.error("좌표를 찾을 수 없습니다:", selectedRi);
          setFormData((prev) => ({
            ...prev,
            [name]: value,
          }));
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // 아이디 변경 시 중복체크 리셋 (등록 모드에서만)
    if (name === "deviceId" && !isEditMode) {
      setIsDeviceIdChecked(false);
      setIsDeviceIdAvailable(false);
    }

    // 에러 메시지 제거
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // 파일 선택 핸들러
  const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 타입 검증
    if (!file.type.startsWith("image/")) {
      modal.alert("파일 오류", "이미지 파일만 업로드 가능합니다.");
      return;
    }

    // 파일 크기 검증 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      modal.alert("파일 오류", "파일 크기는 10MB 이하여야 합니다.");
      return;
    }

    // 미리보기 생성
    const reader = new FileReader();
    reader.onloadend = () => {
      const newPhotos = [...photos];
      newPhotos[index] = {
        file,
        preview: reader.result as string,
        id: photos[index].id, // 기존 ID 유지
      };
      setPhotos(newPhotos);
    };
    reader.readAsDataURL(file);
  };

  // 지도에서 위치 선택 핸들러
  const handleLocationSelect = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toFixed(8),
      longitude: lng.toFixed(8),
    }));

    // 선택한 좌표와 가장 가까운 지역 찾기
    const nearestRegion = findNearestRegion(lat, lng);
    if (nearestRegion) {
      // 리(里) 레벨인 경우
      if (nearestRegion.parentCode.startsWith("4617") && nearestRegion.parentCode.endsWith("000")) {
        const parentRegion = getRegionByCode(nearestRegion.parentCode);
        if (parentRegion) {
          setFormData((prev) => ({
            ...prev,
            regionDistrict: parentRegion.name,
            locationDetail: nearestRegion.name,
          }));
        }
      }
      // 읍면동 레벨인 경우
      else if (nearestRegion.code.startsWith("4617") && nearestRegion.code.endsWith("000")) {
        setFormData((prev) => ({
          ...prev,
          regionDistrict: nearestRegion.name,
          locationDetail: "",
        }));
      }
    }
  };

  // 아이디 중복 체크
  const checkDeviceId = async () => {
    if (!formData.deviceId) {
      modal.alert("확인", "기기 아이디를 입력해주세요.");
      return;
    }

    try {
      startLoading("중복 확인 중...");
      const response = await checkDeviceIdApi(formData.deviceId);

      if (response.duplicate) {
        modal.alert("중복 확인", "이미 사용중인 아이디입니다.");
        setIsDeviceIdAvailable(false);
      } else {
        modal.alert("중복 확인", "사용 가능한 아이디입니다.");
        setIsDeviceIdAvailable(true);
      }
      setIsDeviceIdChecked(true);
    } catch (error) {
      modal.alert("오류", "아이디 중복 체크 중 오류가 발생했습니다.");
    } finally {
      stopLoading();
    }
  };

  // 유효성 검사
  const validate = () => {
    const newErrors: Record<string, string> = {};

    // 필수 필드 검증
    if (!formData.deviceId) newErrors.deviceId = "기기 아이디를 입력해주세요.";
    if (!isEditMode && (!isDeviceIdChecked || !isDeviceIdAvailable)) {
      newErrors.deviceId = "아이디 중복체크를 해주세요.";
    }
    if (!formData.deviceName) newErrors.deviceName = "기기명을 입력해주세요.";
    if (!formData.installationDate) newErrors.installationDate = "설치일자를 선택해주세요.";
    if (!formData.latitude || !formData.longitude) {
      newErrors.location = "설치 위치를 지도에서 선택해주세요.";
    }

    // 이메일 형식 검증
    if (formData.sellerEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.sellerEmail)) {
        newErrors.sellerEmail = "올바른 이메일 형식이 아닙니다.";
      }
    }

    // 연락처 형식 검증
    const phoneRegex = /^01[0-9]-?\d{3,4}-?\d{4}$/;
    if (formData.sellerContact && !phoneRegex.test(formData.sellerContact.replace(/-/g, ""))) {
      newErrors.sellerContact = "올바른 연락처 형식이 아닙니다.";
    }
    if (
      formData.installerContact &&
      !phoneRegex.test(formData.installerContact.replace(/-/g, ""))
    ) {
      newErrors.installerContact = "올바른 연락처 형식이 아닙니다.";
    }

    return newErrors;
  };

  // 폼 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      modal.alert("입력 오류", "필수 항목을 모두 입력해주세요.");
      return;
    }

    try {
      startLoading(isEditMode ? "기기 정보 수정 중..." : "기기 정보 등록 중...");

      // 1. 기기 정보 등록/수정
      const deviceData = {
        ...formData,
        measurementItems: formData.measurementItems.split(",").map((item) => item.trim()),
        regionCode: selectedDistrictCode, // 지역 코드 추가
      };

      let deviceId: number;
      if (isEditMode) {
        await updateDeviceApi(parseInt(id!), deviceData);
        deviceId = parseInt(id!);
      } else {
        const response = await createDeviceApi(deviceData);
        deviceId = response.id;
      }

      // 2. 새로운 사진 업로드
      const uploadPromises = photos
        .map((photo, index) => {
          if (photo.file) {
            const formData = new FormData();
            formData.append("file", photo.file);
            formData.append("deviceId", deviceId.toString());
            formData.append("photoType", `파일선택${index + 1}`);
            return uploadDevicePhotoApi(formData);
          }
          return null;
        })
        .filter(Boolean);

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      modal.alert(
        isEditMode ? "수정 완료" : "등록 완료",
        isEditMode
          ? "기기 정보가 성공적으로 수정되었습니다."
          : "기기 정보가 성공적으로 등록되었습니다."
      );
      navigate("/device");
    } catch (error: any) {
      modal.alert(
        isEditMode ? "수정 실패" : "등록 실패",
        error.message || "처리 중 오류가 발생했습니다."
      );
    } finally {
      stopLoading();
    }
  };

  // 취소
  const handleCancel = () => {
    modal.confirm(
      isEditMode ? "수정 취소" : "등록 취소",
      "작성중인 내용이 모두 사라집니다. 취소하시겠습니까?",
      () => navigate("/device")
    );
  };

  const [mapCenter, setMapCenter] = useState({
    lat: 35.0156,
    lng: 126.7108,
  });

  // formData의 좌표가 변경될 때마다 mapCenter 업데이트
  useEffect(() => {
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
      }
    }
  }, [formData.latitude, formData.longitude]);

  return (
    <div className="device-register-container">
      <form onSubmit={handleSubmit} className="device-form">
        <div className="form-grid">
          {/* 좌측: 기기정보 */}
          <div className="form-section">
            <h2 className="section-title">기기정보</h2>
            <div className="dvc_dv01">
              <table>
                <tr>
                  <th>
                    <label htmlFor="deviceId">
                      아이디 <span className="required">*</span>
                    </label>
                  </th>
                  <td>
                    <div className="input-group">
                      <input
                        type="text"
                        id="deviceId"
                        name="deviceId"
                        value={formData.deviceId}
                        onChange={handleChange}
                        className={`form-input ${errors.deviceId ? "error" : ""}`}
                        placeholder="기기 아이디를 입력하세요"
                        disabled={isEditMode}
                      />
                      {!isEditMode && (
                        <button type="button" onClick={checkDeviceId} className="btn-check">
                          중복체크
                        </button>
                      )}
                    </div>
                    {errors.deviceId && <span className="error-message">{errors.deviceId}</span>}
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="brand">브랜드</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="brand"
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="브랜드명을 입력하세요"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="measurementMethod">측정방식</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="measurementMethod"
                      name="measurementMethod"
                      value={formData.measurementMethod}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    {" "}
                    <label htmlFor="measurementItems">측정항목</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="measurementItems"
                      name="measurementItems"
                      value={formData.measurementItems}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="콤마로 구분하여 입력"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="sensingMethod">센싱방법</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="sensingMethod"
                      name="sensingMethod"
                      value={formData.sensingMethod}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="purchaser">구입일</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="purchaser"
                      name="purchaser"
                      value={formData.purchaser}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="구입일을 입력하세요"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="warrantyPeriod">AS기간</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="warrantyPeriod"
                      name="warrantyPeriod"
                      value={formData.warrantyPeriod}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="purchaseLocation">구매처</label>
                  </th>
                  <td>
                    {" "}
                    <input
                      type="text"
                      id="purchaseLocation"
                      name="purchaseLocation"
                      value={formData.purchaseLocation}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="구매처를 입력하세요"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="vendor">판매처</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="vendor"
                      name="vendor"
                      value={formData.vendor}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="판매처를 입력하세요"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="sellerName">판매자</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="sellerName"
                      name="sellerName"
                      value={formData.sellerName}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="판매자명을 입력하세요"
                    />
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="sellerContact">판매자 연락처</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="sellerContact"
                      name="sellerContact"
                      value={formData.sellerContact}
                      onChange={handleChange}
                      className={`form-input ${errors.sellerContact ? "error" : ""}`}
                      placeholder="010-0000-0000"
                    />
                    {errors.sellerContact && (
                      <span className="error-message">{errors.sellerContact}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="sellerEmail">판매자 이메일</label>
                  </th>
                  <td>
                    <input
                      type="email"
                      id="sellerEmail"
                      name="sellerEmail"
                      value={formData.sellerEmail}
                      onChange={handleChange}
                      className={`form-input ${errors.sellerEmail ? "error" : ""}`}
                      placeholder="email@example.com"
                    />
                    {errors.sellerEmail && (
                      <span className="error-message">{errors.sellerEmail}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>
                    <label htmlFor="otherInfo">기타정보</label>
                  </th>
                  <td>
                    <textarea
                      id="otherInfo"
                      name="otherInfo"
                      value={formData.otherInfo}
                      onChange={handleChange}
                      className="form-textarea"
                      rows={5}
                      placeholder="기타 정보를 입력하세요"
                    />
                  </td>
                </tr>
              </table>
            </div>
          </div>

          {/* 우측: 설치정보 */}
          <div className="form-section2">
            <div className="dvc_dv02">
              <h2 className="section-title">설치정보</h2>
              <p className="section-subtitle">
                (기기정보는 사업장 정보에 종속되며,{" "}
                <span>
                  모니터링 지도에는 기기 위치가 아닌 기기가 종속된 사업장 위치가 표시됩니다.
                </span>
                )
              </p>
            </div>

            <div className="dvc_wrap01">
              <div className="dvc_wrt01">
                {/* 기기 설치 지역 */}
                <div className="subsection">
                  <h3 className="subsection-title">기기 설치 지역</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <input type="text" value={province} className="form-select" disabled />
                    </div>

                    <div className="form-group">
                      <input type="text" value={city} className="form-select" disabled />
                    </div>

                    <div className="form-group">
                      <select
                        name="regionDistrict"
                        value={formData.regionDistrict}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="">읍/면/동 선택</option>
                        {najuDistricts.map((district) => (
                          <option key={district.code} value={district.name}>
                            {district.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* 선택된 읍면동의 하위 리 선택 */}
                  {districts.length > 0 && (
                    <div className="form-group">
                      <select
                        name="locationDetail"
                        value={formData.locationDetail || ""}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="">리 선택 (선택사항)</option>
                        {districts.map((district) => (
                          <option key={district.code} value={district.name}>
                            {district.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* 기기 설치 지역 상세 */}
                <div className="subsection">
                  <h3 className="subsection-title">기기 설치 지역 상세</h3>
                  <div className="form-row2">
                    <div className="form-row-in">
                      <div className="form-group">
                        <label htmlFor="latitude">위도</label>
                        <input
                          type="text"
                          id="latitude"
                          name="latitude"
                          value={formData.latitude}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="위도 (예: 35.0156)"
                          // readOnly 제거
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="longitude">경도</label>
                        <input
                          type="text"
                          id="longitude"
                          name="longitude"
                          value={formData.longitude}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="경도 (예: 126.7108)"
                          // readOnly 제거
                        />
                      </div>
                    </div>
                  </div>
                  {/* 지도 */}
                  <div className="map-container">
                    <MapComponent
                      center={mapCenter}
                      onLocationSelect={handleLocationSelect}
                      height="300px"
                    />
                  </div>
                  {errors.location && <span className="error-message">{errors.location}</span>}
                </div>
                {/* 기기 설치 주변 상세위치 설명 */}
                <div className="form-group">
                  <h3 className="subsection-title">기기 설치 주변 상세위치 설명</h3>
                  <textarea
                    name="locationNote"
                    value={formData.locationNote}
                    onChange={handleChange}
                    className="form-textarea"
                    rows={4}
                    placeholder="기기 설치 주변 상세위치 설명을 기록해주세요"
                  />
                </div>
              </div>

              <div className="dvc_wrt02" style={{ marginTop: "0px" }}>
                {/* 기기 정보 */}
                <div className="dvc_dv01">
                  <table>
                    <tr>
                      <th>
                        <label htmlFor="deviceName">
                          기기명 <span className="required">*</span>
                        </label>
                      </th>
                      <td>
                        <input
                          type="text"
                          id="deviceName"
                          name="deviceName"
                          value={formData.deviceName}
                          onChange={handleChange}
                          className={`form-input ${errors.deviceName ? "error" : ""}`}
                          placeholder="기기명을 입력하세요"
                        />
                        {errors.deviceName && (
                          <span className="error-message">{errors.deviceName}</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <label htmlFor="deviceModel">기기형</label>
                      </th>
                      <td>
                        <input
                          type="text"
                          id="deviceModel"
                          name="deviceModel"
                          value={formData.deviceModel}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="1센서"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        {" "}
                        <label htmlFor="installationDate">
                          설치일자 <span className="required">*</span>
                        </label>
                      </th>
                      <td>
                        <input
                          type="date"
                          id="installationDate"
                          name="installationDate"
                          value={formData.installationDate}
                          onChange={handleChange}
                          className={`form-input ${errors.installationDate ? "error" : ""}`}
                        />
                        {errors.installationDate && (
                          <span className="error-message">{errors.installationDate}</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <label htmlFor="installer">설치담당자</label>
                      </th>
                      <td>
                        <input
                          type="text"
                          id="installer"
                          name="installer"
                          value={formData.installer}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="담당자명을 입력하세요"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <label htmlFor="installerContact">담당자 연락처</label>
                      </th>
                      <td>
                        <input
                          type="text"
                          id="installerContact"
                          name="installerContact"
                          value={formData.installerContact}
                          onChange={handleChange}
                          className={`form-input ${errors.installerContact ? "error" : ""}`}
                          placeholder="010-0000-0000"
                        />
                        {errors.installerContact && (
                          <span className="error-message">{errors.installerContact}</span>
                        )}
                      </td>
                    </tr>
                  </table>
                </div>

                {/* 사진 업로드 */}
                <div className="photo-upload-section">
                  <h3 className="subsection-title">
                    기기 설치 사진 (휴대폰 <strong>세로비율</strong> 3:4)-클릭시 확대창 보입니다.
                  </h3>

                  <div className="photo-upload-grid">
                    {photos.map((photo, index) => (
                      <div key={index} className="photo-upload-box">
                        {photo.preview ? (
                          <img
                            src={photo.preview}
                            alt={`사진 ${index + 1}`}
                            className="photo-preview"
                          />
                        ) : (
                          <div className="photo-placeholder">
                            <span>이미지</span>
                          </div>
                        )}
                        <label htmlFor={`photo-${index}`} className="btn-file-select">
                          파일선택
                        </label>
                        <input
                          type="file"
                          id={`photo-${index}`}
                          accept="image/*"
                          onChange={(e) => handleFileChange(index, e)}
                          style={{ display: "none" }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* 점검 및 관리사항 */}
                <div className="form-group">
                  <h3 className="subsection-title">점검 및 관리사항</h3>
                  <textarea
                    name="inspectionNotes"
                    value={formData.inspectionNotes}
                    onChange={handleChange}
                    className="form-textarea"
                    rows={4}
                    placeholder="기기 분해정황, 점검 및 교체 관련 내용을 기록해주세요"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={handleCancel}>
            취소
          </button>
          <button type="submit" className="btn-submit">
            {isEditMode ? "수정하기" : "등록하기"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeviceFormView;
